import type { TOC } from '@ember/component/template-only';
import Heading from './heading';
import type { HeadingLevel } from './heading';
import type { ComponentLike } from '@glint/template';

/**
 * Color theme for the icon.
 *
 * All 15 names are preserved for API back-compat with the 20+ tio-admin
 * + tio-employee consumers, but they collapse onto **6 TIO canonical
 * visual variants** via the `.app-section-panel--<color>` CSS modifier —
 * see `addons/tio-ui/src/styles/`. Same mapping as IconBadge:
 *
 *   gray                                        → neutral
 *   emerald, green                              → success
 *   teal, cyan, sky, blue, indigo               → info (teal)
 *   violet, purple                              → brand (purple)
 *   pink, rose, red                             → danger
 *   amber, orange                               → warning
 */
export type SectionPanelIconColor =
  | 'gray'
  | 'emerald'
  | 'green'
  | 'teal'
  | 'cyan'
  | 'sky'
  | 'blue'
  | 'indigo'
  | 'violet'
  | 'purple'
  | 'pink'
  | 'rose'
  | 'red'
  | 'amber'
  | 'orange';

interface SectionPanelSignature {
  Args: {
    /** Phosphor icon component to display */
    icon: ComponentLike<{ Args: { size: number }; Element: SVGElement }>;
    /** Title text for the header */
    title: string;
    /** Color for the icon */
    iconColor?: SectionPanelIconColor;
    /** Icon size in CSS pixels. Defaults to the shared 24px heading icon size. */
    iconSize?: number;
    /**
     * Semantic heading level for the panel title. The visual panel-title role
     * stays fixed when the document level changes. Default: 2.
     */
    headingLevel?: HeadingLevel;
    /**
     * Deprecated and ignored. It documented a default of `true` while the
     * template applied the 12px step unless it was passed, so 100 of 106 call
     * sites rendered off the standard panel radius. A panel now always takes
     * the standard surface; for a panel nested inside another panel, pass
     * `class="app-section-panel--rounded-lg"`.
     */
    roundedXl?: boolean;
  };
  Blocks: {
    /** Optional content on the right side of the header (e.g., badge, button) */
    trailing?: [];
    /** Main body content */
    body: [];
  };
  Element: HTMLDivElement;
}

/**
 * Card with icon heading, body, and optional trailing content; the wrapper owns --section-icon-
 * color.
 */
const SectionPanel: TOC<SectionPanelSignature> = <template>
  <div
    data-test-section-panel
    class="app-section-panel app-section-panel--{{if @iconColor @iconColor 'gray'}}"
    ...attributes
  >
    <div data-test-section-panel-header class="app-section-panel-header">
      <div class="app-section-panel-header-content">
        {{#let @icon as |Icon|}}
          <Icon
            @size={{if @iconSize @iconSize 24}}
            class="app-section-panel-icon {{unless @iconSize 'tio-icon-default'}}"
          />
        {{/let}}
        <Heading
          data-test-section-panel-title
          @level={{if @headingLevel @headingLevel 2}}
          @role="panel-title"
        >
          {{@title}}
        </Heading>
      </div>
      {{#if (has-block "trailing")}}
        <div data-test-section-panel-trailing class="app-section-panel-trailing">
          {{yield to="trailing"}}
        </div>
      {{/if}}
    </div>
    <div data-test-section-panel-body class="app-section-panel-body">
      {{yield to="body"}}
    </div>
  </div>
</template>;

export default SectionPanel;

export type { SectionPanelSignature };
